/**
 * Sessions tab — live streaming sessions on this pool (human-readable org
 * and app names) plus a per-app activity histogram from session_usage.
 *
 * Live rows come from the workers' activeJobs (10s auto-refresh); names and
 * history come from /api/admin/pools/:id/session-analytics. Recon-probe
 * sessions are excluded from the histogram server-side.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import { formatDuration, type OrchestratorInfo } from './shared';

interface AppNameEntry { name: string; orgId: string; orgName: string }

interface SessionAnalytics {
  ok: boolean;
  hours: number;
  bucketMs: number;
  histogram: Array<{ appId: string | null; bucketTs: number; sessions: number; minutes: number }>;
  apps: Record<string, AppNameEntry>;
}

export interface SessionsTabProps {
  poolId: string;
  workers: OrchestratorInfo[];
  authHeaders: () => Promise<Record<string, string>>;
  apiBaseUrl: string;
}

type RangeKey = '24h' | '7d';
const RANGE_HOURS: Record<RangeKey, number> = { '24h': 24, '7d': 168 };

// Grayscale series ramp (Bauhaus): identity by darkness step, assigned by
// total volume rank, never cycled — apps beyond the ramp fold into "Other".
const SERIES_SHADES = ['var(--gray-12, #111)', '#4a4a4a', '#7a7a7a', '#a3a3a3', '#c9c9c9'];
const OTHER_SHADE = '#e2e2e2';

export function SessionsTab({ poolId, workers, authHeaders, apiBaseUrl }: SessionsTabProps) {
  const [range, setRange] = useState<RangeKey>('24h');
  const [data, setData] = useState<SessionAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async (r: RangeKey) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/session-analytics?hours=${RANGE_HOURS[r]}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json() as SessionAnalytics);
    } catch (err: any) {
      setError(err.message || 'Failed to load session analytics');
    } finally {
      setLoading(false);
    }
  }, [poolId, authHeaders, apiBaseUrl]);

  useEffect(() => { fetchAnalytics(range); }, [fetchAnalytics, range]);

  const appName = useCallback((appId: string): { app: string; org: string } => {
    const entry = data?.apps?.[appId];
    return { app: entry?.name ?? appId, org: entry?.orgName ?? '—' };
  }, [data]);

  // Live sessions from connected workers' active jobs.
  const liveSessions = useMemo(() => {
    return workers
      .flatMap(w => w.activeJobs.map(j => ({ ...j, worker: w })))
      .sort((a, b) => b.startedAt - a.startedAt);
  }, [workers]);

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Active Sessions ({liveSessions.length})</h2>
        </div>
        {liveSessions.length === 0 ? (
          <div className={styles.empty}>No active streaming sessions</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Application</th>
                <th>Version</th>
                <th>Session</th>
                <th>Worker</th>
                <th>Region</th>
                <th>Running</th>
              </tr>
            </thead>
            <tbody>
              {liveSessions.map(s => {
                const n = appName(s.appId);
                return (
                  <tr key={s.sessionId}>
                    <td>{n.org}</td>
                    <td>{n.app}</td>
                    <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--text-xs)' }}>{s.versionId ?? '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--text-xs)' }}>{s.sessionId}</td>
                    <td>{s.worker.workerId}</td>
                    <td>{s.worker.region}{s.worker.zone ? `/${s.worker.zone}` : ''}</td>
                    <td title={new Date(s.startedAt).toISOString()}>{formatDuration(Date.now() - s.startedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>App Activity</h2>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {(['24h', '7d'] as RangeKey[]).map(r => (
              <button
                key={r}
                type="button"
                className={range === r ? styles.tabButtonActive : styles.tabButton}
                aria-pressed={range === r}
                onClick={() => setRange(r)}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.formHelp}>
          Session minutes served by this pool per {range === '24h' ? 'hour' : '6-hour bucket'}, stacked by application.
          Recon probes excluded.
        </div>
        {error && <div className={styles.empty}>{error}</div>}
        {!error && loading && !data && <div className={styles.loading}>Loading…</div>}
        {!error && data && <AppHistogram data={data} />}
      </div>
    </>
  );
}

function AppHistogram({ data }: { data: SessionAnalytics }) {
  const { histogram, bucketMs, hours, apps } = data;

  const model = useMemo(() => {
    if (histogram.length === 0) return null;

    // Rank apps by total minutes; top N get their own series, rest → Other.
    const totals = new Map<string, number>();
    for (const row of histogram) {
      const key = row.appId ?? 'unknown';
      totals.set(key, (totals.get(key) ?? 0) + row.minutes);
    }
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]);
    const topApps = ranked.slice(0, SERIES_SHADES.length).map(([id]) => id);
    const hasOther = ranked.length > topApps.length;

    // Continuous bucket axis over the whole window, so quiet hours render
    // as gaps rather than being silently skipped.
    const end = Math.floor(Date.now() / bucketMs) * bucketMs;
    const start = end - Math.ceil((hours * 3_600_000) / bucketMs - 1) * bucketMs;
    const buckets: number[] = [];
    for (let ts = start; ts <= end; ts += bucketMs) buckets.push(ts);

    const seriesKeys = hasOther ? [...topApps, '__other'] : topApps;
    const byBucket = new Map<number, Map<string, number>>();
    for (const row of histogram) {
      const key = row.appId && topApps.includes(row.appId) ? row.appId : '__other';
      let m = byBucket.get(row.bucketTs);
      if (!m) { m = new Map(); byBucket.set(row.bucketTs, m); }
      m.set(key, (m.get(key) ?? 0) + row.minutes);
    }

    const maxStack = Math.max(1, ...buckets.map(ts => {
      const m = byBucket.get(ts);
      if (!m) return 0;
      let sum = 0;
      for (const v of m.values()) sum += v;
      return sum;
    }));

    return { buckets, seriesKeys, byBucket, maxStack, totals, topApps, hasOther };
  }, [histogram, bucketMs, hours]);

  if (!model) {
    return <div className={styles.empty}>No session activity recorded in this window</div>;
  }

  const { buckets, seriesKeys, byBucket, maxStack, totals, topApps, hasOther } = model;

  // Chart geometry — viewBox units; rendered responsive via width 100%.
  const W = 900;
  const H = 220;
  const PAD_L = 44;
  const PAD_B = 22;
  const PAD_T = 8;
  const plotW = W - PAD_L - 8;
  const plotH = H - PAD_T - PAD_B;
  const barGap = 2;
  const barW = Math.max(1, plotW / buckets.length - barGap);

  const shade = (key: string) =>
    key === '__other' ? OTHER_SHADE : SERIES_SHADES[topApps.indexOf(key)] ?? OTHER_SHADE;

  const yTicks = [0, 0.5, 1].map(f => Math.round(maxStack * f));

  const fmtBucket = (ts: number) => {
    const d = new Date(ts);
    return buckets.length > 30
      ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Label roughly every ~6 buckets, always including the first.
  const labelEvery = Math.max(1, Math.round(buckets.length / 6));

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 560, display: 'block' }} role="img" aria-label="Session minutes per bucket, stacked by application">
          {/* Y grid + labels (session minutes) */}
          {yTicks.map(v => {
            const y = PAD_T + plotH - (v / maxStack) * plotH;
            return (
              <g key={v}>
                <line x1={PAD_L} x2={W - 8} y1={y} y2={y} stroke="var(--border, #ddd)" strokeWidth="1" />
                <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--text-tertiary, #888)">{v}</text>
              </g>
            );
          })}
          <text x={PAD_L - 34} y={PAD_T + 10} fontSize="9" fill="var(--text-tertiary, #888)">min</text>

          {/* Stacked bars */}
          {buckets.map((ts, i) => {
            const m = byBucket.get(ts);
            if (!m) return null;
            const x = PAD_L + i * (plotW / buckets.length);
            let yCursor = PAD_T + plotH;
            const total = seriesKeys.reduce((s, k) => s + (m.get(k) ?? 0), 0);
            return (
              <g key={ts}>
                <title>{`${fmtBucket(ts)} — ${total} min total`}</title>
                {seriesKeys.map(k => {
                  const v = m.get(k) ?? 0;
                  if (v === 0) return null;
                  const h = (v / maxStack) * plotH;
                  yCursor -= h;
                  return (
                    <rect
                      key={k}
                      x={x}
                      y={yCursor + 1}
                      width={barW}
                      height={Math.max(0, h - 1)}
                      fill={shade(k)}
                    />
                  );
                })}
              </g>
            );
          })}

          {/* X labels (pool-local browser time) */}
          {buckets.map((ts, i) => (
            i % labelEvery === 0 ? (
              <text
                key={ts}
                x={PAD_L + i * (plotW / buckets.length) + barW / 2}
                y={H - 6}
                textAnchor="middle"
                fontSize="10"
                fill="var(--text-tertiary, #888)"
              >
                {fmtBucket(ts)}
              </text>
            ) : null
          ))}
        </svg>
      </div>

      {/* Legend — identity + totals, most active first */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
        {topApps.map(id => {
          const entry = data.apps[id] as AppNameEntry | undefined;
          return (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <span style={{ width: 10, height: 10, background: shade(id), display: 'inline-block' }} />
              <span>
                {entry?.name ?? id}
                <span style={{ color: 'var(--text-tertiary)' }}> ({entry?.orgName ?? 'unknown org'}) — {totals.get(id)} min</span>
              </span>
            </span>
          );
        })}
        {hasOther && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <span style={{ width: 10, height: 10, background: OTHER_SHADE, display: 'inline-block' }} />
            <span>Other</span>
          </span>
        )}
      </div>
    </>
  );
}
